/*
 * AI Workforce page — locked visual asset with percentage-based hotspot overlay.
 *
 * The uploaded `/ai-workforce-original.png` (1536×1024) is Kit's approved artwork
 * and is rendered pixel-exact via <picture> (WebP → PNG fallback). Every
 * clickable element in the artwork is an <a> or <button> placed on top of the
 * image using percentage coordinates so the hotspots scale with the container
 * on every viewport. No pixel-fixed coords.
 *
 * Coord basis: percentages of the 1536×1024 source image. All coords measured
 * from the image; verified with a mobile-tap screenshot pass.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, MessageSquare, Calendar, Users, BarChart3, Search,
  Headphones, ClipboardList, TrendingUp, Mail, Megaphone,
  Rocket, Package, ArrowRight, X,
} from "lucide-react";

// GA / analytics event dispatcher. Falls back to console when GA isn't
// present so we don't lose events in dev.
function trackEvent(name, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    } else {
      console.debug("[analytics]", name, params);
    }
  } catch { /* ignore */ }
}

// ── Hotspot definitions ────────────────────────────────────────────────────
// Each entry: { id, event, aria, to (Link) OR href (a) , left, top, width, height }
// Coordinates are % of the 1536×1024 source image (measured from the artwork).
const NAV_HOTSPOTS = [
  { id: "phone-calls",   event: "ai_phone_calls_clicked",   aria: "Phone Calls — AI Receptionist details",         to: "/ai-receptionist",              left: 25.1, top: 12.7, width: 5.6, height: 6.8 },
  { id: "website-chat",  event: "ai_website_chat_clicked",  aria: "Website Chat — AI Receptionist details",        to: "/ai-receptionist",              left: 40.1, top: 12.7, width: 5.6, height: 6.8 },
  { id: "scheduling",    event: "ai_scheduling_clicked",    aria: "Scheduling — appointment automation details",    to: "/ai-receptionist",              left: 25.1, top: 21.5, width: 5.6, height: 6.8 },
  { id: "crm",           event: "ai_crm_clicked",           aria: "CRM — Lead Qualification and CRM integration",   to: "/ai-sales-bot",                 left: 40.6, top: 21.5, width: 5.6, height: 6.8 },
  { id: "analytics",     event: "ai_analytics_clicked",     aria: "Analytics — Marketing and reporting details",    to: "/custom-studio",                left: 25.1, top: 29.9, width: 5.6, height: 6.8 },
  { id: "google-search", event: "ai_google_search_clicked", aria: "Google Search — SEO Watch Bot details",          to: "/seo-monitoring",               left: 40.1, top: 29.9, width: 5.6, height: 6.8 },
];

const BOT_HOTSPOTS = [
  { id: "ai-receptionist",         event: "ai_receptionist_clicked",         aria: "AI Receptionist — details",         to: "/ai-receptionist",             left: 4.9,  top: 45.4, width: 28.3, height: 16.1 },
  { id: "lead-qualification-bot",  event: "lead_qualification_bot_clicked",  aria: "Lead Qualification Bot — details",  to: "/ai-sales-bot",                left: 35.5, top: 45.4, width: 28.3, height: 16.1 },
  { id: "sales-bot",               event: "sales_bot_clicked",               aria: "Sales Bot — details",               to: "/ai-sales-bot",                left: 66.1, top: 45.4, width: 28.3, height: 16.1 },
  { id: "follow-up-bot",           event: "follow_up_bot_clicked",           aria: "Follow-Up Bot — details",           to: "/ai-receptionist",             left: 4.9,  top: 62.5, width: 28.3, height: 15.6 },
  { id: "seo-watch-bot",           event: "seo_watch_bot_clicked",           aria: "SEO Watch Bot — details",           to: "/seo-monitoring",              left: 35.5, top: 62.5, width: 28.3, height: 15.6 },
  { id: "marketing-bot",           event: "marketing_bot_clicked",           aria: "Marketing Bot — details",           to: "/custom-studio",               left: 66.1, top: 62.5, width: 28.3, height: 15.6 },
];

const DIVISION_HOTSPOTS = [
  { id: "website-launch", event: "website_launch_clicked", aria: "Website Launch — AI-ready foundation",  to: "/website-launch", left: 63.2, top: 79.0, width: 15.9, height: 7.3 },
  { id: "custom-studio",  event: "custom_studio_clicked",  aria: "Custom Studio — complete AI workforce", to: "/custom-studio",  left: 79.4, top: 79.0, width: 16.0, height: 7.3 },
];

const ACTION_HOTSPOTS = [
  { id: "design-ai-system", event: "design_ai_system_clicked", aria: "Design My AI System — start the consultation form",  to: "/#contact?intent=ai_system", left: 25.9, top: 93.5, width: 17.1, height: 5.1 },
  { id: "see-bots-work",    event: "see_bots_work_clicked",    aria: "See How the Bots Work — full bots page",              to: "/bots",                      left: 44.2, top: 93.5, width: 16.0, height: 5.1 },
];

const ALL_HOTSPOTS = [...NAV_HOTSPOTS, ...BOT_HOTSPOTS, ...DIVISION_HOTSPOTS, ...ACTION_HOTSPOTS];

// Bot semantic content (SEO / screen-reader). Server-rendered so crawlers see it.
const BOT_DESCRIPTIONS = [
  { key: "ai-twin",                 name: "AI Twin",                icon: Users,        role: "Your AI Twin learns your services, pricing rules, preferred language and sales process so every bot responds with the knowledge and personality of your business — 24/7 coverage, trained on your business, with human handoff when it matters." },
  { key: "ai-receptionist",         name: "AI Receptionist",        icon: Headphones,   role: "Answers website chats, captures caller details, handles common questions, and schedules appointments. Business result: fewer missed opportunities." },
  { key: "lead-qualification-bot",  name: "Lead Qualification Bot", icon: ClipboardList,role: "Asks the right questions, identifies urgency, budget and service fit, then scores and routes the lead. Business result: your team focuses on real buyers." },
  { key: "sales-bot",               name: "Sales Bot",              icon: TrendingUp,   role: "Explains services, handles approved objections, recommends the next step and hands hot prospects to a person. Business result: more conversations move toward a decision." },
  { key: "follow-up-bot",           name: "Follow-Up Bot",          icon: Mail,         role: "Sends approved text and email follow-ups, appointment reminders and quote check-ins based on lead status. Business result: leads do not disappear after first contact." },
  { key: "seo-watch-bot",           name: "SEO Watch Bot",          icon: Search,       role: "Checks titles, canonicals, broken pages, sitemap health and indexing changes, then reports problems. Business result: SEO failures are caught earlier." },
  { key: "marketing-bot",           name: "Marketing Bot",          icon: Megaphone,    role: "Drafts campaigns, social posts and offers from approved business information, then tracks engagement. Business result: consistent marketing with less manual work." },
];

// ── Component ──────────────────────────────────────────────────────────────
export default function AIWorkforce() {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    document.title = "AI Workforce · PlugitAI — Your Business Never Has to Go Quiet Again";
    // Update meta description + canonical (SPA seo bootstrap for this route).
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description",
      "Six specialized AI teammates — AI Receptionist, Lead Qualification, Sales, Follow-Up, SEO Watch and Marketing bots — answer, qualify, follow up, market and monitor your business day and night."
    );
    setMeta("property", "og:title", "AI Workforce · PlugitAI");
    setMeta("property", "og:image", "/ai-workforce-original.png");
    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", "https://plugitai.com/ai-workforce");
  }, []);

  // Keyboard: Esc closes the zoomed viewer
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setZoomed(false); };
    if (zoomed) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);

  const IMG_ALT = "PlugitAI AI Workforce showing six connected business automation bots and their responsibilities";

  return (
    <main className="min-h-screen bg-black text-white" data-testid="ai-workforce-page">
      {/* Hero — server-rendered H1 for SEO (not inside the image) */}
      <header className="max-w-[1536px] mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-4">
        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] font-black text-blue-400 mb-3">
          PlugitAI AI Workforce
        </div>
        <h1 className="font-black leading-[1.04] tracking-[-0.02em] text-white"
            style={{ fontSize: "clamp(1.75rem, 4vw, 3.25rem)", fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}>
          Your Business Never Has to Go Quiet Again.
        </h1>
        <p className="mt-3 max-w-[720px] text-sm md:text-base text-white/70">
          Six specialized AI teammates answer, qualify, follow up, market and monitor your business — day and night.
        </p>
      </header>

      {/* Image + Hotspot overlay */}
      <section className="max-w-[1536px] mx-auto px-4 md:px-8">
        <div
          className="relative w-full"
          style={{ aspectRatio: "1536 / 1024" }}
          data-testid="ai-workforce-image-container"
        >
          <picture>
            <source srcSet="/ai-workforce.webp" type="image/webp" />
            <img
              src="/ai-workforce-original.png"
              alt={IMG_ALT}
              width="1536"
              height="1024"
              loading="eager"
              decoding="async"
              className="block w-full h-full select-none"
              style={{ objectFit: "contain" }}
              data-testid="ai-workforce-image"
            />
          </picture>

          {/* Hotspots — invisible layer on top of the artwork. Focus outline
              stays visible for keyboard users; visual artwork is untouched. */}
          <div className="absolute inset-0" role="group" aria-label="AI Workforce interactive regions">
            {ALL_HOTSPOTS.map((h) => (
              <Hotspot key={h.id} spot={h} />
            ))}
          </div>

          {/* Mobile "Tap to enlarge" — surfaces the full-screen viewer if the
              image text becomes hard to read on small viewports. */}
          <button
            type="button"
            onClick={() => setZoomed(true)}
            className="md:hidden absolute bottom-2 right-2 text-[10px] uppercase tracking-[0.22em] font-black px-3 py-1.5 rounded-full"
            style={{ background: "rgba(0,0,0,0.65)", color: "#FFF", border: "1px solid rgba(255,255,255,0.3)", backdropFilter: "blur(6px)" }}
            data-testid="ai-workforce-zoom-btn"
            aria-label="Tap to enlarge the AI Workforce diagram"
          >
            Tap to enlarge
          </button>
        </div>
      </section>

      {/* Semantic bot content — real HTML so search engines + screen readers
          can index every role & description. Not just image text. */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="ai-workforce-semantic">
        <h2 className="font-black leading-[1.05] tracking-[-0.02em] mb-8"
            style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)", fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}>
          Six Bots. Six Jobs. One Connected System.
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {BOT_DESCRIPTIONS.map((b) => {
            const Icon = b.icon;
            return (
              <article
                key={b.key}
                id={b.key}
                className="rounded-lg p-5 border border-white/10 bg-white/[0.02] scroll-mt-24"
                data-testid={`bot-explainer-${b.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center bg-blue-500/10 border border-blue-500/40">
                    <Icon size={16} className="text-blue-400" />
                  </div>
                  <h3 className="font-black text-base leading-tight">{b.name}</h3>
                </div>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">{b.role}</p>
              </article>
            );
          })}
        </div>

        {/* CTA row — matches the bottom-of-image actions but as full first-class buttons */}
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/#contact?intent=ai_system"
            onClick={() => trackEvent("design_ai_system_clicked", { location: "semantic_cta" })}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] bg-blue-500 text-black hover:bg-blue-400 transition-colors"
            data-testid="cta-design-ai-system-semantic"
          >
            <Rocket size={14} /> Design My AI System <ArrowRight size={14} />
          </Link>
          <Link
            to="/bots"
            onClick={() => trackEvent("see_bots_work_clicked", { location: "semantic_cta" })}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] border border-white/25 hover:border-white transition-colors"
            data-testid="cta-see-bots-work-semantic"
          >
            <Package size={14} /> See How the Bots Work
          </Link>
        </div>
      </section>

      {/* Full-screen viewer — same image + hotspots stay clickable */}
      {zoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/95 overflow-auto px-2 py-4"
          role="dialog"
          aria-modal="true"
          aria-label="AI Workforce diagram enlarged"
          data-testid="ai-workforce-zoom-viewer"
        >
          <button
            type="button"
            onClick={() => setZoomed(false)}
            className="fixed top-3 right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20"
            aria-label="Close enlarged view"
            data-testid="ai-workforce-zoom-close"
          >
            <X size={18} />
          </button>
          <div className="relative w-full" style={{ aspectRatio: "1536 / 1024", minWidth: "0" }}>
            <picture>
              <source srcSet="/ai-workforce.webp" type="image/webp" />
              <img
                src="/ai-workforce-original.png"
                alt={IMG_ALT}
                width="1536"
                height="1024"
                className="block w-full h-full"
                style={{ objectFit: "contain" }}
              />
            </picture>
            <div className="absolute inset-0">
              {ALL_HOTSPOTS.map((h) => <Hotspot key={h.id + "-zoom"} spot={h} />)}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

// ── Hotspot component ──────────────────────────────────────────────────────
function Hotspot({ spot }) {
  const style = {
    position: "absolute",
    left: `${spot.left}%`,
    top: `${spot.top}%`,
    width: `${spot.width}%`,
    height: `${spot.height}%`,
    minWidth: 44,   // WCAG minimum touch target
    minHeight: 44,
  };
  const onClick = () => trackEvent(spot.event, { hotspot: spot.id });

  // Internal routes use <Link>; anything external (unlikely here) falls back to <a>.
  const isHash = spot.to && spot.to.includes("#");
  const isExternal = spot.to && /^https?:\/\//.test(spot.to);

  const commonProps = {
    style,
    onClick,
    "aria-label": spot.aria,
    "data-testid": `hotspot-${spot.id}`,
    className:
      "block rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 " +
      "hover:bg-blue-400/10 hover:ring-2 hover:ring-blue-400/60 transition-colors",
  };

  const srText = <span className="sr-only">{spot.aria}</span>;

  if (isExternal) {
    return <a href={spot.to} target="_blank" rel="noreferrer" {...commonProps}>{srText}</a>;
  }
  if (isHash) {
    // react-router-dom v6 handles hashes on <Link>
    return <Link to={spot.to} {...commonProps}>{srText}</Link>;
  }
  return <Link to={spot.to} {...commonProps}>{srText}</Link>;
}
